import React from 'react';
import cn from 'classnames';
import moment from 'moment';
import { SelectOptionElement, useDidUpdateEffect } from '@odin-labs/components';
import {
  useGetAvailableJobsiteWorkersLazyQuery,
  WorkerCardType,
} from 'apollo/generated/client-operations';
import { debounce } from 'utils';
import {
  FormInputTypes,
  GridColSpan,
  TypedFormInputs,
  UseFormMethods,
  UseInputs,
} from 'components/form';
import { HardHatIcon } from 'components/icons';
import { BadgeChecklist } from 'components/badgeChecklist/BadgeChecklist';
import { getWorkersOptions, WorkerOptionElement } from 'containers/utils';
import { useBadgesToDeactivate } from 'containers/jobsite/tabs/jobsiteTempBadges/modals/tempBadgeModal/components/useBadgesToDeactivate';
import { AssignTempBadgeFormData } from './types';

export const useLoadWorkerOptions = (args: {
  jobsiteId: string;
}): {
  loadWorkerOptions: (search: string) => Promise<WorkerOptionElement[]>;
} => {
  const { jobsiteId } = args;

  const [fetchJobsiteWorkers] = useGetAvailableJobsiteWorkersLazyQuery();

  const debouncedWorkerSearch = React.useMemo(
    () =>
      debounce(
        async (
          search: string,
          resolve: (value: WorkerOptionElement[]) => void,
        ) => {
          const { data } = await fetchJobsiteWorkers({
            variables: {
              jobsiteId,
              jobsiteJobsiteWorkersInput: {
                search,
                paginationInput: { limit: 100, offset: 0 },
              },
            },
          });
          const result = getWorkersOptions(
            data?.getJobsite?.jobsiteWorkers?.edges.map(({ node }) => node),
          );
          resolve(result);
        },
        300,
      ),
    [fetchJobsiteWorkers, jobsiteId],
  );

  const loadWorkerOptions = React.useCallback(
    async (search: string): Promise<WorkerOptionElement[]> => {
      if (!search || search.length < 2) return [];

      return new Promise((resolve) => {
        debouncedWorkerSearch(search, resolve);
      });
    },
    [debouncedWorkerSearch],
  );

  return { loadWorkerOptions };
};

type GetFormInputsArgs = {
  loadWorkerOptions: (search: string) => Promise<SelectOptionElement[]>;
  onWorkerSelected?: (option: WorkerOptionElement | null) => void;
};

const getFormInputs = ({
  loadWorkerOptions,
}: GetFormInputsArgs): TypedFormInputs<AssignTempBadgeFormData> => ({
  workerId: {
    element: FormInputTypes.AsyncSelect,
    label: 'Worker',
    elementProps: {
      placeholder: 'Type to search',
      loadOptions: loadWorkerOptions,
      icon: HardHatIcon,
    },
    validation: { required: true },
    layout: cn('odin-col-span-6'),
  },
  expirationDate: {
    element: FormInputTypes.DatePicker,
    elementProps: {
      label: 'Expiration Date',
      minDate: moment(),
      showDefaultIcon: true,
      numberOfMonths: 1,
    },
    validation: { required: true },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  tempBadgeReason: {
    element: FormInputTypes.Field,
    label: 'Reason',
    validation: { required: true },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  badges: {
    element: FormInputTypes.CustomInput,
    elementProps: {
      customInput: BadgeChecklist,
    },
    layout: GridColSpan.SpanFull,
  },
});

export const getFormInputsHook =
  ({
    loadWorkerOptions,
    onWorkerSelected,
  }: GetFormInputsArgs): UseInputs<AssignTempBadgeFormData> =>
  (
    form: UseFormMethods<AssignTempBadgeFormData>,
  ): TypedFormInputs<AssignTempBadgeFormData> => {
    const { watch, setValue, getValues } = form;
    const workerIdOption = watch('workerId') as WorkerOptionElement | null;
    const workerId = workerIdOption?.value;

    useDidUpdateEffect(() => {
      onWorkerSelected?.(workerIdOption ?? null);
    }, [workerIdOption, onWorkerSelected]);

    const badgesToDeactivate = useBadgesToDeactivate({
      workerId,
      cardType: WorkerCardType.Proximity,
    });

    React.useEffect(() => {
      const badges = getValues('badges');
      if (JSON.stringify(badges) !== JSON.stringify(badgesToDeactivate)) {
        setValue('badges', badgesToDeactivate);
      }
    }, [badgesToDeactivate]);

    return React.useMemo(
      () => getFormInputs({ loadWorkerOptions }),
      [loadWorkerOptions],
    );
  };

export const getFormDefaultValues = (): AssignTempBadgeFormData => {
  return {
    workerId: null,
    expirationDate: moment().add({ day: 1 }),
    tempBadgeReason: '',
    badges: [],
  };
};
